import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { create } from 'zustand'
import { IconCheck } from './icons'

/**
 * Lightweight toast (mockup `.toast`). A tiny zustand store so any component can fire one via
 * `useToast().show(msg)`. The single `<Toast>` instance is portalled into the shadow-root
 * overlay container (a sibling of `.ti`-content, outside the container-query root) so its
 * `position: fixed` resolves against the viewport, matching the dialogs.
 */
interface ToastState {
  message: string
  /** Bumped on every show() so repeat messages re-trigger the timer. */
  nonce: number
  show: (message: string) => void
}

export const useToast = create<ToastState>((set) => ({
  message: '',
  nonce: 0,
  show: (message) => set((s) => ({ message, nonce: s.nonce + 1 })),
}))

export function Toast({ container }: { container: HTMLElement | null }) {
  const { message, nonce } = useToast()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!nonce) return
    setVisible(true)
    const t = setTimeout(() => setVisible(false), 1700)
    return () => clearTimeout(t)
  }, [nonce])

  if (!container) return null
  return createPortal(
    <div className={`toast ${visible ? 'show' : ''}`} role="status" aria-live="polite">
      <IconCheck />
      <span>{message}</span>
    </div>,
    container,
  )
}
